<template>
    <div class="image-cropper">
        <div v-if="showAvatar" class="profile-setter">
            <SwitchToggle
                :disabled="isAvatar"
                :initialState="isAvatar"
                @switch-checked="$emit('is-profile', image, true)"
            />
            <p class="title">Profile Picture</p>
        </div>
        <div class="image-area">
            <cropper
                ref="cropper"
                class="cropper"
                :src="previewSrc"
                :resizeImage="{
                    wheel: false
                }"
                :stencil-props="stencilProps"
                :stencil-size="
                    isAvatar ? { width: 344, height: 195 } : { width: 400, height: 400 }
                "
                :width="344"
                :height="195"
                :move-image="isAvatar"
                :resize-image="isAvatar"
                :stencil-component="circleCrop ? 'circle-stencil' : 'rectangle-stencil'"
                @change="change"
            />
        </div>
        <div class="image-zoomer">
            <button @click="() => decreaseZoom()" class="decrease-zoom">
                <img src="/images/icons/minus.svg" alt="" class="minus-sign" />
            </button>
            <vue-slider
                v-model="zoomRep"
                :width="304"
                :height="8"
                :min="0"
                :max="1"
                :drag-on-click="true"
                :dot-options="{
                    color: '#8b8b8b',
                    tooltip: 'none',
                    style: {
                        background: '#8b8b8b',
                        width: '20px',
                        height: '20px',
                        position: 'absolute',
                        bottom: '-2px',
                        border: 'none'
                    },
                    focusStyle: { background: '#dedede', border: 'none', boxShadow: 'none' }
                }"
                :rail-style="{ background: '#464646' }"
                :process-style="{ background: '#cdeb09' }"
                :dot-attrs="{ 'aria-label': 'Zoom Settings' }"
            />
            <button @click="() => increaseZoom()" class="increase-zoom">
                <img src="/images/icons/plus.svg" alt="" class="plus-sign" />
            </button>
        </div>
    </div>
</template>
<script>
    import SwitchToggle from '@/components/ui/toggle/Switch.vue'
    import { Cropper } from 'vue-advanced-cropper'
    import VueSlider from 'vue-slider-component'
    import 'vue-slider-component/theme/antd.css'
    import 'vue-advanced-cropper/dist/style.css'
    import 'vue-advanced-cropper/dist/theme.classic.css'

    export default {
        name: 'ImageCropper',
        components: {
            SwitchToggle,
            Cropper,
            VueSlider
        },
        props: {
            image: {
                required: true
            },
            isAvatar: {
                type: Boolean,
                required: true
            },
            croppedImage: {
                required: false
            },
            showAvatar: {
                type: Boolean,
                required: false,
                default: true
            },
            circleCrop: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        emits: ['update:cropped-image', 'is-profile'],
        data: () => ({
            zoomRep: 0
        }),
        computed: {
            previewSrc() {
                if (this.image instanceof File) {
                    return URL.createObjectURL(this.image)
                } else {
                    return this.image.src
                }
            },
            stencilProps() {
                if (this.isAvatar) {
                    return {
                        aspectRatio: 344 / 195,
                        handlers: {},
                        movable: true,
                        resizable: false,
                        width: 344,
                        height: 195
                    }
                } else {
                    return {
                        aspectRatio: 1,
                        handlers: {},
                        lines: {},
                        movable: false,
                        resizable: false,
                        width: 400,
                        height: 400
                    }
                }
            },
            fileName() {
                const name = this.image.name ?? this.image.src
                const lastDot = name.lastIndexOf('.')

                const fileName = name.substring(0, lastDot)
                const ext = name.substring(lastDot + 1)

                return {
                    name: fileName,
                    ext
                }
            }
        },
        watch: {
            zoomRep: function (rep, prevRep) {
                let factor

                if (rep - prevRep === 1) {
                    factor = 2
                } else if (prevRep - rep === 1) {
                    factor = 0.5
                }
                this.$refs.cropper.zoom(factor)
            }
        },
        methods: {
            change({ canvas }) {
                canvas.getContext('2d').drawImage(canvas, 0, 0, canvas.width, canvas.height)

                canvas.toBlob((blob) => {
                    const file = new File(
                        [blob],
                        `${this.fileName.name}-cropped-${canvas.width}x${canvas.height}.${this.fileName.ext}`,
                        { type: 'image/png' }
                    )
                    this.$emit('update:cropped-image', file)
                })
            },
            increaseZoom() {
                if (this.zoomRep + 1 <= 1) {
                    this.zoomRep = this.zoomRep + 1
                }
            },
            decreaseZoom() {
                if (this.zoomRep - 1 >= 0) {
                    this.zoomRep = this.zoomRep - 1
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    .image-cropper {
        p {
            margin-bottom: 0px;
            font-family: 'Instrument Sans';
        }
        button {
            border: none;
        }

        display: flex;
        flex-direction: column;
        width: 100%;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 24px;

        .profile-setter {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;

            .title {
                color: #464646;
                font-size: 16px;
                font-style: normal;
                font-weight: 500;
                line-height: 125%;
            }
        }
        .image-area {
            .cropper {
                height: 400px;
                width: 400px;
                background: #ddd;
                margin-left: auto;
                margin-right: auto;
            }
        }
        .image-zoomer {
            margin-top: -6px;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;

            .decrease-zoom,
            .increase-zoom {
                width: 40px;
                height: 40px;
                background: none;
                padding: 10px;

                img {
                    width: 20px;
                    height: 20px;
                    object-fit: cover;
                }
            }
        }
    }
</style>
